import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { StarPicker } from "../components/PipelineBits";
import { inputCls } from "../hooks/useMasterMaps";

const OUTCOMES = ["Selected", "Rejected", "On Hold", "No Show"];
const OUTCOME_CLS = {
  Selected: "bg-[#1B7F3B] text-white border-[#1B7F3B]",
  Rejected: "bg-[#BB162B] text-white border-[#BB162B]",
  "On Hold": "bg-[#6B7280] text-white border-[#6B7280]",
  "No Show": "bg-[#D97706] text-white border-[#D97706]",
};

export default function FeedbackForm() {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [rt, setRt] = useState(0);
  const [rc, setRc] = useState(0);
  const [ra, setRa] = useState(0);
  const [outcome, setOutcome] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/interviews/${interviewId}`).then(({ data }) => setData(data))
      .catch((e) => toast.error(apiError(e)));
  }, [interviewId]);

  if (!data) return <div className="text-sm text-[#6B7280] py-10 text-center">Loading…</div>;
  const { interview: iv, application: a, candidate: c } = data;
  const noShow = outcome === "No Show";
  const ready = outcome && (noShow || (rt && rc && ra));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/interviews/${interviewId}/feedback`, {
        outcome, rating_technical: rt || null, rating_communication: rc || null,
        rating_attitude: ra || null, feedback_text: text });
      setDone(true);
      toast.success("Feedback recorded — candidate unblocked");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (done || iv.feedback_submitted_on) {
    return (
      <div className="max-w-md mx-auto text-center py-12" data-testid="feedback-done">
        <CheckCircle2 className="mx-auto text-[#1B7F3B]" size={40} />
        <div className="mt-3 font-serif text-xl text-[#0E2240]">Feedback {done ? "submitted" : "already recorded"}</div>
        <div className="text-sm text-[#6B7280] mt-1">{a.candidate_name} · Round {iv.round_no}{iv.outcome && ` · ${iv.outcome}`}</div>
        <button onClick={() => navigate("/interviews")} className="mt-5 px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50">Back to interviews</button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto" data-testid="feedback-form-page">
      <div className="bg-[#0E2240] rounded-sm p-4 text-white">
        <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Interview feedback · Round {iv.round_no}</div>
        <div className="font-serif text-2xl mt-1">{a.candidate_name}</div>
        <div className="text-xs text-white/70 mt-0.5">{a.desig_name} · {a.branch_name} · {a.req_no}</div>
        <div className="text-[10px] text-white/50 mt-1">{c.total_experience_years}y exp · expects ₹{Number(c.expected_ctc).toLocaleString()} · {c.notice_period_days}d notice</div>
      </div>

      <div className="bg-white border border-gray-200 border-t-0 rounded-b-sm p-5 space-y-5">
        {[["Technical / job knowledge", rt, setRt, "rate-tech"],
          ["Communication", rc, setRc, "rate-comm"],
          ["Attitude & culture fit", ra, setRa, "rate-att"]].map(([label, val, set, tid]) => (
          <div key={tid} className="flex items-center justify-between">
            <span className="text-sm text-[#0E2240]">{label}</span>
            <StarPicker value={val} onChange={set} testid={tid} />
          </div>
        ))}

        <div>
          <div className="text-sm text-[#0E2240] mb-2">Outcome</div>
          <div className="grid grid-cols-2 gap-2" data-testid="outcome-buttons">
            {OUTCOMES.map((o) => (
              <button key={o} data-testid={`outcome-${o.replace(/\s/g, "-")}`} onClick={() => setOutcome(o)}
                className={`py-2.5 text-sm rounded-sm border transition-colors duration-200 ${outcome === o ? OUTCOME_CLS[o] : "border-gray-300 text-[#0E2240] hover:bg-gray-50"}`}>
                {o}
              </button>
            ))}
          </div>
        </div>

        <textarea data-testid="feedback-text" className={`${inputCls} min-h-[70px]`} placeholder="One-line comments (optional)"
          value={text} onChange={(e) => setText(e.target.value)} />

        <button data-testid="feedback-submit" disabled={!ready || busy} onClick={submit}
          className="w-full py-3 bg-[#0E2240] text-white text-sm font-medium rounded-sm hover:bg-[#1a3358] transition-colors duration-200 disabled:opacity-40">
          {busy ? "Submitting…" : "Submit feedback"}
        </button>
        {!ready && <div className="text-[10px] text-[#6B7280] text-center">{outcome ? "Rate all three areas" : "Pick an outcome"}{!noShow && outcome ? " (1–5 stars each)" : ""}</div>}
      </div>
    </div>
  );
}
